/**
 * LES TRACÉS FAUTIFS PRÉVUS, CONSTRUITS ET REJOUÉS — chantier « Élève », lot 8,
 * palier L0 (« voir avant de produire », AB#943 ; `docs/pilotage/
 * atelier-figures/CONCEPTION.md`, § 5.1).
 *
 * Un tracé fautif prévu s'écrit par les CONTRAINTES qu'il tient (« pente 1 et
 * ordonnée à l'origine 2 »), jamais par un dessin. Personne ne l'a donc jamais
 * vu, et rien ne prouvait qu'un élève qui le trace reçoive bien l'erreur qu'il
 * vise : un tracé fautif qui tient aussi toutes les contraintes du corrigé est
 * jugé JUSTE ; un autre, plus haut dans la liste, peut le capter ; une
 * contrainte mal écrite peut le rendre introuvable.
 *
 * Ce module fait les deux gestes qui manquaient :
 *
 *  1. `esquisserTraceFautif` CONSTRUIT un tracé qui tient les contraintes du
 *     tracé fautif. Il part de la solution du prof (mêmes objets, mêmes noms)
 *     et la déforme : d'abord par le calcul, pour les contraintes qui le
 *     permettent (un point placé, une droite par sa pente et son ordonnée à
 *     l'origine, une courbe par ses paramètres, un vecteur par ses
 *     composantes…), puis, si cela ne suffit pas, par une recherche bornée sur
 *     les coordonnées de ses points libres, guidée par les écarts que mesure
 *     le vérificateur. Déterministe : ni hasard ni horloge.
 *  2. `eprouverTracesFautifs` JUGE chacun de ces tracés avec `jugerTrace` — la
 *     fonction même du serveur — et dit s'il déclenche l'erreur qu'il vise.
 *
 * Aucun modèle (invariant I-11 de la console) : c'est du calcul.
 */
import { attenduDuTrace, documentDeTrace, repereDeTrace, } from '../types/manuel-exercices.js';
import { sujetDeContrainte } from './contraintes-en-phrases.js';
import { estIdEnonce, resoudreConstruction, } from './construction.js';
import { enonceDeTrace, jugerTrace, } from './juger-trace.js';
import { distance, milieu, plus, unitaire } from './geometrie.js';
import { PHASE_VERIFIEE, verifierConstruction } from './verifier.js';
const estObjet = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const nombre = (v) => typeof v === 'number' && Number.isFinite(v) ? v : null;
/** Six décimales : une coordonnée calculée n'en garde pas plus. */
const net = (v) => Math.round(v * 1e6) / 1e6;
/** Évaluations du vérificateur au plus, pour la recherche (un tracé fautif). */
const EVALUATIONS_MAX = 3000;
/**
 * Construit un tracé qui tient les contraintes d'un tracé fautif prévu, à
 * partir de la solution du prof. `griefs` : aucun tracé trouvé — les
 * contraintes sont peut-être contradictoires, ou hors de ce que la recherche
 * sait déformer (un polygone d'une aire donnée, par exemple).
 */
export function esquisserTraceFautif(exercice, contraintes) {
    const { corrige } = exercice;
    const enonce = enonceDeTrace(exercice.figure);
    const repere = repereDeTrace(corrige, enonce);
    if (repere === null)
        return { griefs: ['l’exercice n’a ni figure d’énoncé ni repère'] };
    const calque = enonce !== null;
    const objets = corrige.solution.map((o) => ({
        ...o,
        def: { ...o.def },
    }));
    const doc = (liste) => documentDeTrace(repere, {
        objets: liste,
        calque,
    });
    const attendu = attenduDuTrace({ cibles: corrige.cibles, contraintes: [...contraintes] }, doc(objets));
    const verifier = (liste) => verifierConstruction(doc(liste), enonce, attendu);
    const tient = (liste) => {
        const v = verifier(liste).verdict;
        return v === 'juste' || v === 'a_ecrire';
    };
    const rendre = (voie) => ({
        objets: objets,
        voie,
    });
    if (tient(objets))
        return rendre('solution');
    /* ── 1. Par le calcul ─────────────────────────────────────────────── */
    const parId = (id) => typeof id === 'string' ? objets.find((o) => o.id === id) : undefined;
    const resolue = () => resoudreConstruction(doc(objets), enonce, { phase: PHASE_VERIFIEE });
    const liaisons = verifier(objets).liaisons;
    /** Le point d'une référence, sur la construction COURANTE. */
    const pointDe = (r) => {
        if (!estObjet(r))
            return null;
        const x = nombre(r.x);
        const y = nombre(r.y);
        if (x !== null && y !== null)
            return { x, y };
        const id = typeof r.enonce === 'string'
            ? estIdEnonce(r.enonce)
                ? r.enonce
                : `e:${r.enonce}`
            : typeof r.cible === 'string'
                ? liaisons[r.cible]
                : null;
        if (typeof id !== 'string')
            return null;
        const o = resolue().objets.get(id);
        return o?.genre === 'point' ? o.p : null;
    };
    /** Pose un point libre de la solution ; `false` : ce n'est pas un point libre. */
    const placer = (id, p) => {
        const o = parId(id);
        if (o === undefined || o.def.type !== 'point_libre')
            return false;
        o.def = { ...o.def, x: net(p.x), y: net(p.y) };
        return true;
    };
    /* La fonction définie par parties : ses parties REMPLACENT les courbes de la solution. */
    const parties = contraintes.find((c) => c.type === 'parties_egales');
    if (parties !== undefined && parties.type === 'parties_egales') {
        for (let i = objets.length - 1; i >= 0; i -= 1) {
            if (objets[i]?.def.type === 'fonction')
                objets.splice(i, 1);
        }
        parties.parties.forEach((p, k) => {
            objets.push({
                id: `fp${k + 1}`,
                def: {
                    type: 'fonction',
                    famille: p.famille,
                    parametres: { ...p.parametres },
                    domaine: [...p.domaine],
                    bouts: [...p.bouts],
                },
            });
        });
    }
    for (const cible of corrige.cibles) {
        const o = parId(liaisons[cible.id]);
        if (o === undefined)
            continue;
        const siennes = contraintes.filter((c) => sujetDeContrainte(c) === cible.id);
        const valeur = (type) => {
            const c = siennes.find((x) => x.type === type);
            return c === undefined ? null : nombre(c.valeur);
        };
        const def = o.def;
        switch (def.type) {
            case 'point_libre': {
                for (const c of siennes) {
                    if (c.type === 'point_en')
                        placer(o.id, { x: c.x, y: c.y });
                    if (c.type === 'milieu') {
                        const de = pointDe(c.de);
                        const a = pointDe(c.a);
                        if (de !== null && a !== null)
                            placer(o.id, milieu(de, a));
                    }
                }
                break;
            }
            case 'droite':
            case 'segment':
            case 'demi_droite': {
                const bouts = def.type === 'demi_droite' ? [def.origine, def.par] : [def.de, def.a];
                const r = resolue().objets.get(o.id);
                if (r?.genre !== 'ligne')
                    break;
                const dir0 = { x: r.b.x - r.a.x, y: r.b.y - r.a.y };
                const m = valeur('pente');
                const b = valeur('ordonnee_origine');
                const longueur = valeur('longueur');
                const par = [];
                for (const c of siennes) {
                    if (c.type === 'droite_par') {
                        const p1 = pointDe(c.p1);
                        const p2 = pointDe(c.p2);
                        if (p1 !== null)
                            par.push(p1);
                        if (p2 !== null)
                            par.push(p2);
                    }
                    else if (c.type === 'passe_par' || c.type === 'sur') {
                        const p = pointDe(c.point);
                        if (p !== null)
                            par.push(p);
                    }
                }
                let P = null;
                let Q = null;
                const [p0, p1] = par;
                if (p0 !== undefined && p1 !== undefined)
                    [P, Q] = [p0, p1];
                else if (m !== null && b !== null)
                    [P, Q] = [
                        { x: 0, y: b },
                        { x: 1, y: b + m },
                    ];
                else if (m !== null && p0 !== undefined)
                    [P, Q] = [p0, { x: p0.x + 1, y: p0.y + m }];
                else if (b !== null && p0 !== undefined)
                    [P, Q] = [{ x: 0, y: b }, p0.x === 0 ? plus({ x: 0, y: b }, dir0) : p0];
                else if (m !== null)
                    [P, Q] = [r.a, { x: r.a.x + 1, y: r.a.y + m }];
                else if (b !== null)
                    [P, Q] = [{ x: 0, y: b }, plus({ x: 0, y: b }, dir0)];
                else if (p0 !== undefined)
                    [P, Q] = [p0, plus(p0, dir0)];
                if (P === null || Q === null)
                    break;
                if (longueur !== null) {
                    const u = unitaire({ x: Q.x - P.x, y: Q.y - P.y });
                    if (u !== null)
                        Q = { x: P.x + u.x * longueur, y: P.y + u.y * longueur };
                }
                placer(bouts[0], P);
                placer(bouts[1], Q);
                break;
            }
            case 'cercle_rayon': {
                const rayon = valeur('rayon');
                if (rayon !== null)
                    o.def = { ...o.def, rayon };
                else {
                    /* Sans rayon : celui qui fait passer le cercle par le point donné. */
                    const c = siennes.find((x) => x.type === 'sur');
                    const res = resolue().objets.get(o.id);
                    const p = c === undefined || c.type !== 'sur' ? null : pointDe(c.point);
                    if (p !== null && res?.genre === 'cercle') {
                        o.def = { ...o.def, rayon: net(distance(res.centre, p)) };
                    }
                }
                break;
            }
            case 'cercle_par_point': {
                const rayon = valeur('rayon');
                const res = resolue().objets.get(o.id);
                if (rayon !== null && res?.genre === 'cercle') {
                    placer(def.par, { x: res.centre.x + rayon, y: res.centre.y });
                }
                break;
            }
            case 'fonction': {
                const c = siennes.find((x) => x.type === 'courbe_egale');
                if (c !== undefined && c.type === 'courbe_egale') {
                    const { domaine: _d, ...reste } = o.def;
                    o.def = {
                        ...reste,
                        famille: c.famille,
                        parametres: { ...c.parametres },
                        ...(c.domaine !== undefined ? { domaine: [...c.domaine] } : {}),
                    };
                }
                break;
            }
            case 'vecteur': {
                const c = siennes.find((x) => x.type === 'vecteur_egal');
                const res = resolue().objets.get(def.de);
                if (c !== undefined && c.type === 'vecteur_egal' && res?.genre === 'point') {
                    placer(def.a, { x: res.p.x + c.x, y: res.p.y + c.y });
                }
                break;
            }
            case 'demi_plan': {
                const c = siennes.find((x) => x.type === 'demi_plan');
                if (c === undefined || c.type !== 'demi_plan')
                    break;
                o.def = { ...o.def, cote: c.cote, stricte: c.stricte };
                const a = c.parametres.a;
                const b = c.parametres.b ?? 0;
                const frontiere = parId(def.frontiere);
                if (frontiere?.def.type === 'fonction') {
                    frontiere.def = { ...frontiere.def, famille: 'lineaire', parametres: { a, b } };
                }
                else if (frontiere !== undefined &&
                    (frontiere.def.type === 'droite' || frontiere.def.type === 'segment')) {
                    placer(frontiere.def.de, { x: 0, y: b });
                    placer(frontiere.def.a, { x: 1, y: a + b });
                }
                break;
            }
            default:
                break;
        }
    }
    if (tient(objets))
        return rendre('calcul');
    /* ── 2. Par la recherche ──────────────────────────────────────────── */
    const variables = [];
    for (const o of objets) {
        if (o.def.type === 'point_libre')
            variables.push({ o, cle: 'x' }, { o, cle: 'y' });
        if (o.def.type === 'cercle_rayon')
            variables.push({ o, cle: 'rayon' });
    }
    /** Le coût : ce qui ne tient pas, et de combien (l'écart borné). */
    const cout = () => verifier(objets).constats.reduce((s, c) => c.etat === 'tenue' ? s : s + 1 + (c.ecart === null ? 2 : Math.min(10, Math.abs(c.ecart))), 0);
    let evaluations = 0;
    let courant = cout();
    let pas = Math.max(1, (repere.xMax - repere.xMin) / 10);
    while (pas > 1 / 256 && evaluations < EVALUATIONS_MAX && variables.length > 0) {
        let mieux = false;
        for (const v of variables) {
            for (const signe of [1, -1]) {
                const avant = nombre(v.o.def[v.cle]) ?? 0;
                const essai = net(avant + signe * pas);
                if (v.cle === 'rayon' && essai <= 0)
                    continue;
                v.o.def = { ...v.o.def, [v.cle]: essai };
                evaluations += 1;
                const c = cout();
                if (c < courant - 1e-12) {
                    courant = c;
                    mieux = true;
                    if (tient(objets))
                        return rendre('recherche');
                    break;
                }
                v.o.def = { ...v.o.def, [v.cle]: avant };
            }
            if (evaluations >= EVALUATIONS_MAX)
                break;
        }
        if (!mieux)
            pas /= 2;
    }
    if (tient(objets))
        return rendre('recherche');
    return {
        griefs: [
            'aucun tracé trouvé qui tienne toutes les contraintes de ce tracé fautif, en partant de la solution',
        ],
    };
}
/** Les états qui disent qu'un tracé fautif NE déclenche PAS son erreur. */
export const ETATS_TRACE_FAUTIF_EN_DEFAUT = new Set([
    'juge_juste',
    'autre_erreur',
    'aucune_erreur',
    'incomplet',
    'non_construit',
    'sans_erreur',
]);
/** Construit et juge chaque tracé fautif prévu de l'exercice, dans l'ordre du corrigé. */
export function eprouverTracesFautifs(exercice) {
    return (exercice.corrige.tracesFautifs ?? []).map((t) => {
        const fautive = exercice.fautives.find((f) => f.reponse === t.reponse);
        const base = {
            reponse: t.reponse,
            contraintes: t.contraintes,
            erreurVisee: fautive?.erreurCode ?? null,
        };
        const esquisse = esquisserTraceFautif(exercice, t.contraintes);
        if ('griefs' in esquisse) {
            return {
                ...base,
                etat: 'non_construit',
                objets: null,
                jugement: null,
                griefs: esquisse.griefs,
            };
        }
        const jugement = jugerTrace(exercice, esquisse.objets);
        const etat = fautive === undefined
            ? 'sans_erreur'
            : jugement.genre === 'incomplet'
                ? 'incomplet'
                : jugement.verdict === 'juste'
                    ? 'juge_juste'
                    : jugement.erreurCode === fautive.erreurCode
                        ? 'declenche'
                        : jugement.erreurCode === null
                            ? 'aucune_erreur'
                            : 'autre_erreur';
        return { ...base, etat, objets: esquisse.objets, jugement, griefs: [] };
    });
}
/** Ce que dit, en une phrase, l'état d'un tracé fautif rejoué (pour le relecteur). */
export function direEtatTraceFautif(e, titre) {
    const nom = (code) => code === null ? 'aucune erreur' : `« ${titre?.(code) ?? code} »`;
    switch (e.etat) {
        case 'declenche':
            return `Déclenche bien ${nom(e.erreurVisee)}.`;
        case 'juge_juste':
            return 'Ne déclenche rien : ce tracé tient toutes les contraintes du corrigé, l’élève lirait « C’est juste ».';
        case 'autre_erreur':
            return `Ne déclenche pas son erreur : le vérificateur nomme ${nom(e.jugement?.genre === 'juge' ? e.jugement.erreurCode : null)} à la place.`;
        case 'aucune_erreur':
            return 'Ne déclenche pas son erreur : le tracé est jugé à revoir, sans erreur nommée.';
        case 'incomplet':
            return `Ne déclenche rien : le tracé construit n’est pas un essai (« ${e.jugement?.retroaction ?? ''} »).`;
        case 'non_construit':
            return `Ne peut pas être dessiné : ${e.griefs.join(' ; ')}.`;
        case 'sans_erreur':
            return 'Ne peut rien déclencher : aucune réponse fautive de ce libellé ne nomme une erreur.';
    }
}
//# sourceMappingURL=traces-fautifs.js.map